import { useCallback, useEffect, useState } from 'react';
import type { Alert, AlertStatus } from '@vigilo/shared';
import { get } from '@vigilo/api-client';
import { CROWD_SIMULATION_RESET_EVENT } from './useSimulation';
import {
  CROWD_PAGINATION_DEFAULT_PAGE_SIZE,
  type CrowdPaginationEnvelope,
} from '../components/PaginationControls';

export type PagedAlertFilterStatus = 'active' | AlertStatus | 'all';

interface AlertListSummary {
  total: number;
  active: number;
  open: number;
  acknowledged: number;
  escalated: number;
  resolved: number;
}

interface AlertListResponse {
  data?: Alert[];
  summary?: AlertListSummary;
  pagination?: CrowdPaginationEnvelope;
}

export interface PagedAlertsState {
  alerts: Alert[];
  pagination: CrowdPaginationEnvelope;
  loading: boolean;
  error: string | null;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  refresh: () => void;
}

const EMPTY_PAGINATION: CrowdPaginationEnvelope = {
  total: 0,
  page: 1,
  pageSize: CROWD_PAGINATION_DEFAULT_PAGE_SIZE,
  hasNextPage: false,
};

function unwrapAlerts(body: unknown): AlertListResponse {
  if (Array.isArray(body)) {
    return {
      data: body as Alert[],
      pagination: {
        total: body.length,
        page: 1,
        pageSize: body.length || CROWD_PAGINATION_DEFAULT_PAGE_SIZE,
        hasNextPage: false,
      },
    };
  }
  const direct = body as AlertListResponse;
  if (Array.isArray(direct?.data)) return direct;
  const envelope = body as { data?: AlertListResponse };
  return envelope.data ?? {};
}

function queryFor(
  operationId: string,
  filterStatus: PagedAlertFilterStatus,
  page: number,
  pageSize: number,
  zoneIds: readonly string[] = [],
): string {
  const params = new URLSearchParams();
  params.set('operationId', operationId);
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));
  params.set('sortBy', 'triggeredAt');
  params.set('sortOrder', 'DESC');
  if (zoneIds.length > 0) params.set('zoneIds', zoneIds.join(','));
  if (filterStatus === 'active') {
    params.set('statuses', 'open,acknowledged,escalated');
  } else if (filterStatus !== 'all') {
    params.set('status', filterStatus);
  }
  return params.toString();
}

export function usePagedAlerts(
  operationId: string | null | undefined,
  filterStatus: PagedAlertFilterStatus,
  enabled = true,
  zoneIds: readonly string[] = [],
): PagedAlertsState {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [pagination, setPagination] = useState<CrowdPaginationEnvelope>(EMPTY_PAGINATION);
  const [page, setPageValue] = useState(1);
  const [pageSize, setPageSizeValue] = useState(CROWD_PAGINATION_DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const zoneIdsKey = zoneIds.join(',');

  useEffect(() => {
    setPageValue(1);
  }, [filterStatus, operationId, zoneIdsKey]);

  useEffect(() => {
    let cancelled = false;
    setError(null);

    if (!enabled || !operationId) {
      setAlerts([]);
      setPagination({ ...EMPTY_PAGINATION, page, pageSize });
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    const scopedZoneIds = zoneIdsKey ? zoneIdsKey.split(',').filter(Boolean) : [];
    get<unknown>(`/alerts?${queryFor(operationId, filterStatus, page, pageSize, scopedZoneIds)}`)
      .then(body => {
        if (cancelled) return;
        const result = unwrapAlerts(body);
        const rows = result.data ?? [];
        const allowedZoneIds = scopedZoneIds.length > 0 ? new Set(scopedZoneIds) : null;
        setAlerts(rows.filter(alert => alert.operationId === operationId && (!allowedZoneIds || allowedZoneIds.has(alert.zoneId))));
        setPagination(result.pagination ?? {
          total: rows.length,
          page,
          pageSize,
          hasNextPage: false,
        });
      })
      .catch(() => {
        if (!cancelled) {
          setAlerts([]);
          setError('Unable to load alerts.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, filterStatus, operationId, page, pageSize, refreshKey, zoneIdsKey]);

  useEffect(() => {
    const reset = () => {
      setAlerts([]);
      setPagination({ ...EMPTY_PAGINATION, page, pageSize });
      setRefreshKey(value => value + 1);
    };
    window.addEventListener(CROWD_SIMULATION_RESET_EVENT, reset);
    return () => window.removeEventListener(CROWD_SIMULATION_RESET_EVENT, reset);
  }, [page, pageSize]);

  const setPage = useCallback((nextPage: number) => {
    setPageValue(Math.max(1, Math.trunc(nextPage)));
  }, []);

  const setPageSize = useCallback((nextPageSize: number) => {
    setPageSizeValue(Math.max(1, Math.trunc(nextPageSize)));
    setPageValue(1);
  }, []);

  const refresh = useCallback(() => {
    setRefreshKey(value => value + 1);
  }, []);

  return { alerts, pagination, loading, error, page, pageSize, setPage, setPageSize, refresh };
}
